import Phaser from 'phaser';

import {
  COLOR_BACKGROUND,
  DEFAULT_LEVEL_URL,
  LEVELCRAFT_URL,
  TILE_SIZE,
} from '../config/feel';
import { validateLevel } from '../../shared/level-format/load';
import { conveyorPieceFor, loadSprites } from '../sprites';
import { SOUND_KEYS, SOUND_VOLUMES, loadSounds } from '../sounds';
import { postToParent, saveLevelToHost } from '../../embed';
import type {
  CardinalDir,
  Cannon as CannonData,
  Conveyor as ConveyorData,
  ConveyorDir,
  Gear as GearData,
  GlassWall as GlassWallData,
  Key as KeyData,
  KeyWall as KeyWallData,
  LaserCannon as LaserCannonData,
  LaserRotateMode,
  LevelData,
  PageData,
  Spike as SpikeData,
  SpikeBlock as SpikeBlockData,
  Teleport as TeleportData,
  TextLabel as TextLabelData,
  Turret as TurretData,
} from '../../shared/level-format/types';
import {
  drawCannon,
  drawCoin,
  drawConveyor,
  drawExit,
  drawGear,
  drawGlassWall,
  drawGridBackground,
  drawKey,
  drawKeyWall,
  drawLaserCannon,
  drawPortal,
  drawSelectionBox,
  drawSpawn,
  drawSpike,
  drawSpikeBlock,
  drawTeleport,
  drawTextLabel,
  drawTurret,
  drawWall,
} from './editor/drawers';
import {
  colorParamHtml,
  dirParamHtml,
  elementParamHtml,
  laserCannonParamHtml,
  teleportParamHtml,
  textToolParamHtml,
} from './editor/paramsHtml';
import type { SelectedElement } from './editor/types';

const LEVEL_KEY = 'editor-level';

// localStorage slot for the standalone build's "try mode" editor (the
// itch.io export, or any bare `index.html` open). There's no backend
// and no dev-server save endpoint there, so Save writes the level JSON
// here and the editor reloads it on the next visit. One slot — try mode
// is a single-scratchpad experience, not a level library. Namespaced so
// it can't collide with anything else on the origin.
const TRY_DRAFT_KEY = 'ricochet:try-draft';

// One ID per placeable thing — direction / color / target-page now live
// in `toolParams` instead of being baked into the tool name. Selecting a
// tool reveals its params panel; param changes update the persistent
// state and the next click uses those values. There is no `select` tool:
// selection happens implicitly when the user clicks an existing element
// (see onPointerDown). With no tool active, the editor is in "pointer
// mode" — clicks select / deselect, drags move elements, but empty
// cells do nothing.
type ToolId =
  | 'wall' | 'coin' | 'glass' | 'spike_block'
  | 'spike' | 'conveyor' | 'cannon' | 'turret' | 'gear' | 'laser_cannon'
  | 'portal' | 'key' | 'key_wall' | 'teleport' | 'text'
  | 'spawn' | 'exit' | 'eraser';

// Tools that support hold-and-drag continuous action. Eraser drags to
// wipe a streak; the placement entries are limited to simple,
// directionless / single-color elements where dragging has an obvious
// meaning (paint a row of walls). Tools with params (cannon direction,
// key color, teleport target) stay click-only so the user doesn't
// accidentally paint a wrong-direction streak.
const DRAG_ENABLED_TOOLS: ReadonlySet<ToolId> = new Set<ToolId>([
  'wall', 'coin', 'glass', 'spike', 'eraser',
]);

// Tools whose placed elements have per-instance params worth showing
// immediately. After a successful placement we auto-select the new
// element so the full params panel (including ones absent from the
// tool-sticky panel, like cannon period or laser delay) is editable
// without a follow-up click. Excluded:
//   wall / coin       — synthesized refs, no editable params
//   spawn / exit      — singletons, no editable params
//   portal            — multi-cell, color is shared per pair (already
//                       on the tool-sticky panel); findElementAt skips
//                       portals by design
//   eraser            — destructive, never selects
const AUTO_SELECT_TOOLS: ReadonlySet<ToolId> = new Set<ToolId>([
  'glass', 'spike_block', 'spike', 'conveyor', 'cannon', 'turret',
  'laser_cannon', 'gear', 'key', 'key_wall', 'teleport', 'text',
]);

// Per-tool sticky params: each entry remembers what the user last picked
// in that tool's params panel. Only listed tools that actually have
// editable params; the rest (wall, coin, glass, spike_block, turret,
// gear, spawn, exit, eraser) place with hard-coded defaults today.
interface ToolParamsState {
  spike: CardinalDir;
  cannon: CardinalDir;
  conveyor: ConveyorDir;
  portal: number;       // 0..KEY_COLORS_LIGHT.length-1
  key: number;
  key_wall: number;
  teleport: number;     // target page index
  laser_cannon: {
    dir: CardinalDir;
    rotate: LaserRotateMode;
    duration: number;   // seconds; 0 = continuous, else stepped 0.5
    downtime: number;   // seconds; stepped 0.5
  };
  text: {
    width: number;     // cells, integer >= 1
    height: number;    // cells, integer >= 1
  };
}

// Per-tool defaults applied at placement time. 13a-3 will add a params
// panel to override these per-instance after placement.
const DEFAULT_GLASS_DELAY = 1.5;
const DEFAULT_CANNON_PERIOD = 2.0;
const DEFAULT_CANNON_BULLET_SPEED = 8.0;
const DEFAULT_TURRET_PERIOD = 2.0;
const DEFAULT_TURRET_BULLET_SPEED = 8.0;
const DEFAULT_GEAR_SIZE = 2;
const DEFAULT_GEAR_SPEED = 3.0;
const DEFAULT_GEAR_SPIN = 4.0;

// Spike + barrel layouts moved to ./editor/drawers; the editor's
// drawers module owns its own copies (PlayScene still has its physics
// copy independently — they happen to match today, may diverge later).

// Phase 13a-1 — level editor scaffolding. Renders the loaded level
// non-interactively (no click-to-place yet — that's 13a-2). What works:
//   - Boot via ?mode=edit
//   - Page navigation (prev / next)
//   - Save level → JSON download
//   - Load level → file picker
//   - Play → hands the in-memory level to PlayScene with no save round-trip
export class EditScene extends Phaser.Scene {
  private level!: LevelData;
  private currentPageIndex = 0;
  // Container holds everything we draw for the current page so a page
  // switch is `removeAll(true)` instead of tracking each game object.
  private pageRoot!: Phaser.GameObjects.Container;
  private palette: HTMLDivElement | null = null;
  // Standalone "try mode" banner — shown only when the editor has no
  // host iframe and no disk path (see isTryMode()). Explains that
  // levels live in localStorage only and links out to LevelCraft.
  private tryModeBanner: HTMLDivElement | null = null;
  // Set once the try-mode "heads up about saving" dialog has been shown,
  // so it pops on the first editor entry of the session and not again on
  // every Play↔Edit round-trip. Deliberately NOT reset in init() — it
  // should survive scene restarts (a page refresh starts a fresh game
  // instance, so it shows again then, which is fine).
  private tryModeDialogShown = false;
  private paramsPanel: HTMLDivElement | null = null;
  private pageLabel: HTMLSpanElement | null = null;
  private fileLabel: HTMLSpanElement | null = null;
  private selectedTool: ToolId | null = null;
  private toolParams: ToolParamsState = {
    spike: 'up',
    cannon: 'right',
    conveyor: 'cw',
    portal: 0,
    key: 0,
    key_wall: 0,
    teleport: 1,  // sensible default: send to page 2; clamped on apply
    laser_cannon: { dir: 'right', rotate: 'none', duration: 3.0, downtime: 3.0 },
    text: { width: 4, height: 2 },
  };

  // Drag-to-paint: set on pointerdown when the active tool supports it
  // (DRAG_PLACEABLE_TOOLS), updated on pointermove to skip cells already
  // painted in this drag, cleared on pointerup. lastCol/lastRow start at
  // the cell we just placed in pointerdown so we don't double-place there.
  private placementDragState: { lastCol: number; lastRow: number } | null = null;

  // Select-tool gesture tracker. `dragGhost` stays null until the
  // pointer leaves the start cell — its presence is the signal "this
  // is a drag, not a click". On pointerup we route to drag-completion
  // (move element) if the ghost was created, or to click-handling
  // (gear-path edit ops) when the up cell matches the down cell.
  // Cleared on pointerup, scene shutdown, tool change, or page change.
  private selectGesture: {
    start: { col: number; row: number };
    dragGhost: Phaser.GameObjects.Rectangle | null;
  } | null = null;

  // Gear path editor — a sub-state of the Select tool. Set when the
  // user clicks (no drag) on an existing gear in Select mode. While
  // active, clicks on empty cells push waypoints, clicks on a different
  // gear switch the edit target, and clicking the current gear's home
  // closes the loop and exits. Reset on tool switch, page change,
  // erase of the gear, or the "Done (open)" action.
  private gearEditState: { gear: GearData } | null = null;

  // Currently-selected element for instance-level editing. Set on a
  // single click in Select mode, cleared on tool switch, page change,
  // erase of the selected element, or click on an empty cell. Drives
  // both the bounding-box highlight and the per-type params panel.
  private selectedElement: SelectedElement | null = null;

  // Path the level was loaded from — also where Save writes back via
  // the dev-server `/api/save-level` endpoint. null means "no known
  // disk path" (e.g. legacy boot with no levelPath); Save degrades to
  // a download in that case so changes aren't lost.
  private levelPath: string | null = null;

  // True iff the level has been modified since the last successful
  // Save (or since loading from disk). Drives the unsaved-changes
  // confirm dialog when the user navigates back to the menu, and is
  // round-tripped through PlayScene so it survives play-testing.
  private dirty = false;

  // Set when embedded inside the LevelCraft web platform. Replaces the
  // dev-server `/api/save-level` flow with a postMessage round-trip
  // (the host page calls Supabase). Persists across Play → Edit
  // round-trips so save still routes through the host afterward.
  private embedLevelId: string | null = null;
  // Bound message handler for `ricochet:start-publish-verify` from the
  // host page. Stored as a field so SHUTDOWN/DESTROY can remove it
  // (re-entering the editor from PlayScene would otherwise stack
  // multiple listeners and double-fire on the next verify request).
  private publishVerifyListener: ((ev: MessageEvent) => void) | null = null;

  constructor() {
    super('EditScene');
  }

  init(data?: {
    level?: LevelData;
    pageIndex?: number;
    levelPath?: string;
    dirty?: boolean;
    embedLevelId?: string;
  }): void {
    // Phaser scenes are SINGLETONS — `scene.start()` re-runs the
    // lifecycle methods on the SAME instance, so any field we don't
    // explicitly reset here leaks across runs. The original code only
    // assigned `this.level` when `data.level` was provided; coming
    // back from MenuScene with just `{ levelPath }` left the previous
    // level's in-memory data sitting in `this.level`, which the
    // `if (!this.level)` checks in preload + create then short-
    // circuited — picking Level 2 from the menu after editing Level
    // 1 silently reused Level 1's data (and Save then wrote it back
    // to Level 2's file on disk).
    this.level = (data?.level ?? undefined) as LevelData;
    this.currentPageIndex = data?.pageIndex ?? 0;
    this.levelPath = data?.levelPath ?? null;
    // dirty is preserved across Play round-trips so unsaved changes
    // don't silently get "forgotten" when the user plays the level
    // and comes back. A fresh load from disk passes no `dirty`, so
    // the default-false is correct there.
    this.dirty = data?.dirty ?? false;
    this.embedLevelId = data?.embedLevelId ?? null;
    // Always reset transient gesture state — neither type should
    // ever survive a scene restart anyway, but be explicit.
    this.placementDragState = null;
    this.selectGesture = null;
    // Selection / gear-edit refs point into `this.level`'s arrays.
    // They stay valid on a Play → Edit round-trip (same in-memory
    // level handed back), but a fresh menu pick replaces the level
    // and orphans them — clear in that case so the orange highlight
    // doesn't anchor at the wrong cell.
    if (!data?.level) {
      this.selectedElement = null;
      this.gearEditState = null;
    }
  }

  preload(): void {
    // Try mode (standalone, no host, no disk path): if a draft was
    // saved to localStorage on a previous visit, resume it instead of
    // the default sandbox so a page refresh doesn't wipe the user's
    // work. A corrupt / incompatible blob is ignored (we fall through
    // to the normal load below).
    if (!this.level && this.isTryMode()) {
      const draft = this.loadTryDraft();
      if (draft) this.level = draft;
    }

    // Only fetch a level file when not handed one via init() (e.g. the
    // editor's Play hand-off carries the in-memory level back). When a
    // levelPath was provided we load that file specifically; otherwise
    // we fall back to the legacy default (kept around as a sandbox).
    if (!this.level) {
      const url = this.levelPath ?? DEFAULT_LEVEL_URL;
      // Phaser's JSON cache is keyed by the asset key, NOT the URL —
      // a second EditScene boot with a different `levelPath` but the
      // same LEVEL_KEY would otherwise serve the previously-cached
      // payload, so picking Level 2 from the menu after editing Level
      // 1 would silently load Level 1's data (and Save would write
      // Level 1's data to Level 2's file). Forcing a fresh fetch by
      // clearing the cache entry first.
      if (this.cache.json.has(LEVEL_KEY)) {
        this.cache.json.remove(LEVEL_KEY);
      }
      this.load.json(LEVEL_KEY, url);
    }

    // All visual sprites (shared with PlayScene). EditScene shows
    // static frames; the AnimationManager defs live in PlayScene only.
    loadSprites(this);
    loadSounds(this);
  }

  create(): void {
    this.cameras.main.setBackgroundColor(COLOR_BACKGROUND);

    // Stop the gameplay BGM if we returned here from a Play test —
    // the editor isn't a music context, and the sound manager is
    // global so the music would otherwise keep playing.
    this.sound.get(SOUND_KEYS.bgm)?.stop();

    // Phaser globally captures any key registered via `addKey()` /
    // `createCursorKeys()` (PlayScene does both for SPACE + arrows)
    // and calls preventDefault on it — which blocks those keys from
    // reaching the editor's textarea (text-label CONTENT field).
    // EditScene needs no keyboard input of its own, so clear the
    // global captures here. PlayScene re-adds them when it boots.
    this.input.keyboard?.clearCaptures();

    if (!this.level) {
      const raw = this.cache.json.get(LEVEL_KEY) as unknown;
      this.level = validateLevel(raw, DEFAULT_LEVEL_URL);
    }

    this.pageRoot = this.add.container(0, 0);
    this.renderPage();
    this.buildPalette();
    if (this.isTryMode()) {
      this.buildTryModeBanner();
      // First editor entry this session: surface the save-persistence
      // caveat as a dialog. The banner stays as the ongoing reminder
      // (and carries the Download button).
      if (!this.tryModeDialogShown) {
        this.tryModeDialogShown = true;
        this.showTryModeDialog();
      }
    }

    // Cell click → apply selected tool. Phaser's `pointer.worldX/Y` already
    // factors in camera scroll + canvas-fit scaling, so we get true world
    // coords without manual conversion.
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) =>
      this.onPointerDown(p),
    );
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (p: Phaser.Input.Pointer) =>
      this.onPointerMove(p),
    );
    this.input.on(Phaser.Input.Events.POINTER_UP, (p: Phaser.Input.Pointer) =>
      this.onPointerUp(p),
    );

    // Listen for the host page's request to start a full-level
    // playthrough for publish verification. Triggered by the platform's
    // PublishToggle when last_cleared_at is NULL (gate stale).
    // Same-origin only (the iframe is served from /games/ricochet/ on
    // the same domain). Ignored when not running embedded.
    if (this.embedLevelId) {
      const expectedId = this.embedLevelId;
      this.publishVerifyListener = (ev: MessageEvent) => {
        if (ev.origin !== window.location.origin) return;
        const msg = ev.data as { type?: string; levelId?: string } | null;
        if (!msg || typeof msg !== 'object') return;
        if (msg.type !== 'ricochet:start-publish-verify') return;
        if (msg.levelId !== expectedId) return;
        // Auto-save first when the editor is dirty — the published
        // version must match what was actually tested. Without this, a
        // user with unsaved edits would verify against the in-memory
        // level but publish the older on-disk one. saveLevel() shows
        // an alert and returns false on failure, so we just abort.
        void (async () => {
          if (this.dirty) {
            const ok = await this.saveLevel();
            if (!ok) return;
          }
          // Force start at page 0 (level spawn). `fromEditor: true`
          // keeps the in-game ◀ Edit button so the user can abort, and
          // routes the post-clear hand-off back to EditScene like any
          // other editor test play.
          this.scene.start('PlayScene', {
            level: this.level,
            pageIndex: 0,
            fromEditor: true,
            publishVerify: true,
            embedLevelId: expectedId,
          });
        })();
      };
      window.addEventListener('message', this.publishVerifyListener);
    }

    // Tear down DOM palette on scene exit so it doesn't linger over
    // PlayScene (or persist after a hot reload).
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cancelDrag();
      this.destroyPalette();
      this.destroyTryModeBanner();
      if (this.publishVerifyListener) {
        window.removeEventListener('message', this.publishVerifyListener);
        this.publishVerifyListener = null;
      }
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.cancelDrag();
      this.destroyPalette();
      this.destroyTryModeBanner();
      if (this.publishVerifyListener) {
        window.removeEventListener('message', this.publishVerifyListener);
        this.publishVerifyListener = null;
      }
    });
  }

  // --- rendering ---------------------------------------------------------

  private renderPage(): void {
    this.pageRoot.removeAll(true);
    const page = this.level.pages[this.currentPageIndex];
    if (!page) return;

    const cols = page.tiles[0]!.length;
    const rows = page.tiles.length;
    const offsetX = (this.scale.gameSize.width - cols * TILE_SIZE) / 2;
    const offsetY = (this.scale.gameSize.height - rows * TILE_SIZE) / 2;
    this.cameras.main.setScroll(-offsetX, -offsetY);

    const root = this.pageRoot;

    drawGridBackground(root, cols, rows);

    for (let r = 0; r < rows; r++) {
      const row = page.tiles[r]!;
      for (let c = 0; c < row.length; c++) {
        const ch = row.charAt(c);
        if (ch === 'W') drawWall(root, c, r);
        else if (ch === 'C') drawCoin(root, c, r);
      }
    }

    page.glass_walls?.forEach((g) => drawGlassWall(root, g.x, g.y));
    page.spike_blocks?.forEach((s) => drawSpikeBlock(root, s.x, s.y));
    // Conveyor adjacency map for left/middle/right piece selection.
    const conveyorMap = new Map<string, ConveyorDir>();
    page.conveyors?.forEach((c) => conveyorMap.set(`${c.x},${c.y}`, c.dir));
    page.conveyors?.forEach((c) =>
      drawConveyor(root, c.x, c.y, c.dir, conveyorPieceFor(conveyorMap, c.x, c.y, c.dir)),
    );
    page.spikes?.forEach((s) => drawSpike(root, s.x, s.y, s.dir));
    page.cannons?.forEach((c) => drawCannon(root, c.x, c.y, c.dir));
    page.key_walls?.forEach((k) => drawKeyWall(root, k.x, k.y, k.color));
    page.keys?.forEach((k) => drawKey(root, k.x, k.y, k.color));
    page.gears?.forEach((g) => drawGear(root, g, this.gearEditState?.gear === g));
    page.portals?.forEach((p) =>
      p.points.forEach((pt) => drawPortal(root, pt.x, pt.y, p.color)),
    );
    page.turrets?.forEach((t) => drawTurret(root, t.x, t.y));
    page.laser_cannons?.forEach((l) => drawLaserCannon(root, l.x, l.y, l.dir));
    page.text_labels?.forEach((tl) => drawTextLabel(root, tl));
    page.teleports?.forEach((t) => drawTeleport(root, t.x, t.y, t.target_page));

    drawSpawn(root, page.spawn.x, page.spawn.y);
    if (this.level.exit.page === this.currentPageIndex) {
      drawExit(root, this.level.exit.x, this.level.exit.y);
    }

    // Selection-box overlay: full bounds for multi-cell text labels,
    // single-cell otherwise. Caller computes pixel-space bounds since
    // the drawer doesn't know about the SelectedElement union.
    if (this.selectedElement) {
      const sel = this.selectedElement;
      const w = sel.kind === 'text_label' ? sel.ref.width * TILE_SIZE : TILE_SIZE;
      const h = sel.kind === 'text_label' ? sel.ref.height * TILE_SIZE : TILE_SIZE;
      drawSelectionBox(root, {
        x: sel.ref.x * TILE_SIZE,
        y: sel.ref.y * TILE_SIZE,
        w,
        h,
      });
    }
  }

  // (Drawer methods moved to ./editor/drawers — renderPage() above
  // composes them.)


  // --- DOM chrome --------------------------------------------------------

  private updateLabels(): void {
    if (this.pageLabel) {
      this.pageLabel.textContent =
        `${this.currentPageIndex + 1} / ${this.level.pages.length}`;
    }
    if (this.fileLabel) {
      this.fileLabel.textContent = this.level.name;
    }
  }

  private changePage(delta: number): void {
    const next = this.currentPageIndex + delta;
    if (next < 0 || next >= this.level.pages.length) return;
    this.currentPageIndex = next;
    // gearEditState + selectedElement reference data on the *previous*
    // page; the user implicitly ends both sub-states by leaving.
    this.gearEditState = null;
    this.selectedElement = null;
    this.cancelDrag();
    this.renderPage();
    this.renderParamsPanel();
    this.updateLabels();
  }

  // Save the level. Three destinations depending on how the editor was
  // launched:
  //   embedded (LevelCraft web platform) → postMessage round-trip; the
  //     host page writes to Supabase under the user's session.
  //   try mode (standalone build, no host, no disk path) → localStorage,
  //     so the scratch level survives a refresh. See isTryMode().
  //   dev / authoring (levelPath set) → POST to the Vite dev-server
  //     middleware, which writes back to public/levels/level-NN.json.
  // Returns true on success, false on any failure (so callers like the
  // unsaved-changes modal can keep the user in the editor when a save
  // fails mid-flow).
  private async saveLevel(): Promise<boolean> {
    const saveBtn = this.palette?.querySelector(
      '[data-action="save"]',
    ) as HTMLButtonElement | null;

    // Embed path: hand the level back to the host page, which calls
    // Supabase under the user's session (RLS enforces ownership).
    if (this.embedLevelId) {
      const result = await saveLevelToHost(this.embedLevelId, this.level);
      if (!result.ok) {
        alert(`Save failed: ${result.error}`);
        return false;
      }
      this.dirty = false;
      this.flashSaveButton(saveBtn);
      return true;
    }

    // Try mode: no backend and no dev-server endpoint — persist to
    // localStorage so the level isn't lost on refresh.
    if (!this.levelPath) {
      if (!this.saveTryDraft()) return false;
      this.dirty = false;
      this.flashSaveButton(saveBtn);
      return true;
    }
    try {
      const response = await fetch('/api/save-level', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ path: this.levelPath, level: this.level }),
      });
      if (!response.ok) {
        const err = (await response.json().catch(() => ({}))) as { error?: string };
        alert(`Save failed: ${err.error ?? response.statusText}`);
        return false;
      }
      this.dirty = false;
      this.flashSaveButton(saveBtn);
      return true;
    } catch (err) {
      alert(
        `Save failed: ${(err as Error).message}\n\n` +
          `The save endpoint only exists when running 'npm run dev'.`,
      );
      return false;
    }
  }

  // Three-way "you have unsaved changes" modal. Returns to the caller
  // via the two callbacks rather than a Promise so the call site can
  // stay synchronous when the user hits Cancel. Clicking outside the
  // dialog or hitting Esc is ignored — the user must pick an action.
  private showUnsavedConfirm(onSave: () => void, onDiscard: () => void): void {
    const modal = document.createElement('div');
    modal.className = 'editor-modal';
    modal.innerHTML = `
      <div class="editor-modal-backdrop"></div>
      <div class="editor-modal-content">
        <p>You have unsaved changes to <b>${this.level.name}</b>.</p>
        <div class="editor-modal-actions">
          <button data-modal-action="cancel" class="editor-modal-btn">Cancel</button>
          <button data-modal-action="discard" class="editor-modal-btn editor-modal-btn-danger">Discard</button>
          <button data-modal-action="save" class="editor-modal-btn editor-modal-btn-primary">Save &amp; Leave</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.addEventListener('click', (ev) => {
      const target = ev.target as HTMLElement;
      const action = target.getAttribute('data-modal-action');
      if (!action) return;
      this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
      if (action === 'save') {
        modal.remove();
        onSave();
      } else if (action === 'discard') {
        modal.remove();
        onDiscard();
      } else if (action === 'cancel') {
        modal.remove();
      }
    });
  }

  private flashSaveButton(btn: HTMLButtonElement | null): void {
    if (!btn) return;
    const original = btn.textContent;
    btn.textContent = 'Saved!';
    btn.classList.add('palette-btn-saved');
    setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove('palette-btn-saved');
    }, 900);
  }

  // Exit the editor. Standalone mode jumps to the level-select menu;
  // embedded mode posts a message so the host page can navigate (the
  // editor lives inside an iframe and can't drive the parent's URL).
  private leaveEditor(): void {
    if (this.embedLevelId) {
      postToParent('exit-edit', { levelId: this.embedLevelId });
      return;
    }
    this.scene.start('MenuScene');
  }

  // Kicks the current level's JSON down as a browser download. Used by
  // the try-mode banner's "Download .json" button so a standalone-build
  // user can keep a file and rebuild the level on levelcraft.gg.
  private downloadLevel(): void {
    const blob = new Blob([JSON.stringify(this.level, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.level.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // --- standalone "try mode" --------------------------------------------

  // True when the editor was opened without a host iframe (embedLevelId)
  // and without a disk path (levelPath) — i.e. the standalone build's
  // "Try the Level Editor" entry, or a bare `?mode=edit` open. There's
  // no backend and no dev-server save endpoint in that situation, so
  // Save goes to localStorage and a banner explains the limits.
  private isTryMode(): boolean {
    return !this.embedLevelId && !this.levelPath;
  }

  // Persist the in-memory level to localStorage. Returns false (and
  // alerts) if storage is unavailable or full — quota is the realistic
  // failure for a single level blob in private-browsing modes.
  private saveTryDraft(): boolean {
    try {
      window.localStorage.setItem(TRY_DRAFT_KEY, JSON.stringify(this.level));
      return true;
    } catch (err) {
      alert(
        `Couldn't save to this browser's storage: ${(err as Error).message}\n\n` +
          `Use "Download .json" in the banner to keep a copy instead.`,
      );
      return false;
    }
  }

  // Read back a previously-saved try-mode draft. Returns null if there
  // is none, or if the stored blob is corrupt / fails level validation
  // (e.g. saved by an older build with an incompatible schema) — the
  // caller then falls back to the default sandbox level.
  private loadTryDraft(): LevelData | null {
    let raw: string | null;
    try {
      raw = window.localStorage.getItem(TRY_DRAFT_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    try {
      return validateLevel(JSON.parse(raw) as unknown, 'localStorage try-mode draft');
    } catch {
      return null;
    }
  }

  // --- tool palette ------------------------------------------------------

  private buildPalette(): void {
    if (this.palette) return;
    const palette = document.createElement('div');
    palette.className = 'editor-palette';
    // Right-edge sidebar holding everything: file chrome on top
    // (EDIT MODE label, page nav, Load / Save / Play), then a 2-column
    // grid of single-purpose tool buttons, then params for the active
    // tool. The top toolbar that previously sat above the canvas was
    // folded in here so the canvas's top rows aren't hidden anymore.
    palette.innerHTML = `
      <div class="palette-section-label">EDIT MODE</div>
      <div class="palette-file-label" data-role="file-label"></div>
      <div class="palette-page-nav">
        <button data-action="prev-page" class="palette-btn palette-stepper-btn">◀</button>
        <span class="palette-stepper-value" data-role="page-label"></span>
        <button data-action="next-page" class="palette-btn palette-stepper-btn">▶</button>
      </div>
      <div class="palette-actions">
        <button data-action="add-page" class="palette-btn">+ Page</button>
        <button data-action="del-page" class="palette-btn">− Page</button>
      </div>
      <div class="palette-actions">
        <button data-action="menu" class="palette-btn">${this.embedLevelId ? 'Done' : 'Menu'}</button>
        <button data-action="save" class="palette-btn">Save</button>
      </div>
      <button data-action="play" class="palette-btn palette-btn-primary palette-btn-full">▶ Play</button>
      <div class="palette-divider"></div>
      <div class="palette-tools">
        <button data-tool="wall" class="palette-btn">Wall</button>
        <button data-tool="coin" class="palette-btn">Coin</button>
        <button data-tool="glass" class="palette-btn">Glass</button>
        <button data-tool="spike_block" class="palette-btn">SBlock</button>
        <button data-tool="spike" class="palette-btn">Spike</button>
        <button data-tool="conveyor" class="palette-btn">Conveyor</button>
        <button data-tool="cannon" class="palette-btn">Cannon</button>
        <button data-tool="turret" class="palette-btn">Turret</button>
        <button data-tool="laser_cannon" class="palette-btn">Laser</button>
        <button data-tool="gear" class="palette-btn">Gear</button>
        <button data-tool="portal" class="palette-btn">Portal</button>
        <button data-tool="key" class="palette-btn">Key</button>
        <button data-tool="key_wall" class="palette-btn">K-Wall</button>
        <button data-tool="teleport" class="palette-btn">Tele</button>
        <button data-tool="text" class="palette-btn">Text</button>
        <button data-tool="spawn" class="palette-btn">Spawn</button>
        <button data-tool="exit" class="palette-btn">Exit</button>
      </div>
      <button data-tool="eraser" class="palette-btn palette-btn-eraser palette-btn-full">Erase</button>
      <div class="palette-params" data-role="params"></div>
    `;
    document.body.appendChild(palette);
    this.palette = palette;
    this.paramsPanel = palette.querySelector('[data-role="params"]') as HTMLDivElement;
    this.pageLabel = palette.querySelector('[data-role="page-label"]');
    this.fileLabel = palette.querySelector('[data-role="file-label"]');
    this.updateLabels();
    palette.addEventListener('click', (ev) => this.onPaletteClick(ev));
    // Event-delegated input listener for the per-instance text-label
    // textarea. We can't re-render the params panel on each keystroke
    // (that destroys the textarea and steals focus mid-typing), so
    // text content updates flow through here directly into the level
    // data + a renderPage call.
    palette.addEventListener('input', (ev) => this.onPaletteInput(ev));
  }

  private destroyPalette(): void {
    if (this.palette) {
      this.palette.remove();
      this.palette = null;
      this.paramsPanel = null;
      this.pageLabel = null;
      this.fileLabel = null;
      this.selectedTool = null;
    }
  }

  // Bottom-left banner shown only in try mode (standalone build). Sets
  // expectations up front: this level lives in this browser only, and
  // the real platform (accounts, community, publishing) is at
  // levelcraft.gg. The "Download .json" button is the bridge — keep a
  // file, rebuild it there. Anchored bottom-left so it clears the
  // right-edge tool palette and the canvas's top rows.
  private buildTryModeBanner(): void {
    if (this.tryModeBanner) return;
    const banner = document.createElement('div');
    banner.className = 'editor-trymode-banner';
    banner.innerHTML = `
      <div class="editor-trymode-text">
        <b>Try mode.</b> Levels you build here are saved in <b>this browser only</b>
        (and lost if you clear site data). To get an account, play community levels,
        or publish online, head to
        <a href="${LEVELCRAFT_URL}" target="_blank" rel="noopener">levelcraft.gg</a>.
      </div>
      <button data-action="download" class="editor-trymode-btn">Download .json</button>
    `;
    banner.querySelector('[data-action="download"]')?.addEventListener('click', () => {
      this.downloadLevel();
    });
    document.body.appendChild(banner);
    this.tryModeBanner = banner;
  }

  private destroyTryModeBanner(): void {
    if (this.tryModeBanner) {
      this.tryModeBanner.remove();
      this.tryModeBanner = null;
    }
  }

  // One-time-per-session heads-up shown when the standalone editor
  // opens (see create()). Same message as the try-mode banner, but as
  // a modal so it actually gets read before the user starts building.
  // Informational, not a decision — dismiss on the button or the dark
  // backdrop; clicks inside the dialog body (the levelcraft.gg link)
  // are ignored so the link works.
  private showTryModeDialog(): void {
    const modal = document.createElement('div');
    modal.className = 'editor-modal';
    modal.innerHTML = `
      <div class="editor-modal-backdrop"></div>
      <div class="editor-modal-content">
        <p>
          <b>Heads up — try mode.</b> Levels you build here are saved in
          <b>this browser only</b>. Save keeps them across page refreshes,
          but clearing site data wipes them and there's no sync to other
          devices. To get an account, play community-made levels, or
          publish your level online, head to
          <a href="${LEVELCRAFT_URL}" target="_blank" rel="noopener">levelcraft.gg</a> —
          and use <b>Download .json</b> (bottom-left) to keep a copy and rebuild it there.
        </p>
        <div class="editor-modal-actions">
          <button data-modal-action="ok" class="editor-modal-btn editor-modal-btn-primary">Got it</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    modal.addEventListener('click', (ev) => {
      const target = ev.target as HTMLElement;
      if (
        target.getAttribute('data-modal-action') === 'ok' ||
        target.classList.contains('editor-modal-backdrop')
      ) {
        this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
        modal.remove();
      }
    });
  }

  // Click router for everything inside the palette. Branches on which
  // data-* attribute the clicked element carries:
  //   data-action     → file/page chrome (prev/next page, load, save, play)
  //   data-tool       → select that tool, re-render params panel
  //   data-param-dir  → set direction param for the active tool
  //   data-param-color→ set color param for the active tool
  //   data-param-step → step the active tool's numeric param by ±1
  private onPaletteClick(ev: MouseEvent): void {
    const target = ev.target as HTMLElement;

    // UI click sound for the "navigation"-class buttons only — file/page
    // actions and tool picks. Steppers, color swatches, direction
    // pickers etc. are continuous editing inputs and would spam the
    // SFX channel; they intentionally stay silent.
    const isNavClick = target.hasAttribute('data-action') || target.hasAttribute('data-tool');
    if (isNavClick) {
      this.sound.play(SOUND_KEYS.uiButton, { volume: SOUND_VOLUMES.sfx });
    }

    const action = target.getAttribute('data-action');
    if (action) {
      this.handlePaletteAction(action);
      return;
    }

    const tool = target.getAttribute('data-tool');
    if (tool) {
      // Click the active tool again → deactivate, returning to the
      // "no tool" pointer mode. Without this, once any tool is picked
      // there'd be no path back to pointer mode, so "click empty to
      // deselect" could never fire.
      const next = this.selectedTool === (tool as ToolId) ? null : (tool as ToolId);
      this.selectedTool = next;
      // Switching (or clearing) tools always ends a gear-path edit
      // session and clears the instance selection. closed flag stays
      // as-is; the user explicitly opens or closes via the
      // gear-finish-open button or click-home.
      const hadSubState = this.gearEditState != null || this.selectedElement != null;
      this.gearEditState = null;
      this.selectedElement = null;
      if (hadSubState) this.renderPage();
      this.updateToolHighlight();
      this.renderParamsPanel();
      return;
    }
    // ---- Instance editing (data-elem-*) ----
    // Routes click attributes prefixed `data-elem-*` straight into the
    // selected element's ref. Each handler narrows the SelectedElement
    // discriminated union before mutating, so unrelated kinds silently
    // ignore inapplicable inputs. Runs BEFORE the `!selectedTool` guard
    // because the element panel is reachable in pointer mode (no tool
    // active) — the user can click an element and edit its params
    // without picking a tool first.
    if (this.selectedElement) {
      const elemDir = target.getAttribute('data-elem-dir');
      if (elemDir) {
        const sel = this.selectedElement;
        if (sel.kind === 'spike' || sel.kind === 'cannon' || sel.kind === 'laser_cannon') {
          sel.ref.dir = elemDir as CardinalDir;
          this.dirty = true;
        }
        this.renderPage();
        this.renderParamsPanel();
        return;
      }
      const convDir = target.getAttribute('data-elem-conveyor-dir');
      if (convDir) {
        if (this.selectedElement.kind === 'conveyor') {
          this.selectedElement.ref.dir = convDir as ConveyorDir;
          this.dirty = true;
        }
        this.renderPage();
        this.renderParamsPanel();
        return;
      }
      const elemColor = target.getAttribute('data-elem-color');
      if (elemColor !== null) {
        const sel = this.selectedElement;
        if (sel.kind === 'key' || sel.kind === 'key_wall') {
          sel.ref.color = parseInt(elemColor, 10);
          this.dirty = true;
        }
        this.renderPage();
        this.renderParamsPanel();
        return;
      }
      const lasRot = target.getAttribute('data-elem-laser-rotate');
      if (lasRot) {
        if (this.selectedElement.kind === 'laser_cannon') {
          this.selectedElement.ref.rotate = lasRot as LaserRotateMode;
          this.dirty = true;
        }
        this.renderParamsPanel();
        return;
      }
      const toggle = target.getAttribute('data-elem-toggle');
      if (toggle === 'closed' && this.selectedElement.kind === 'gear') {
        this.selectedElement.ref.closed = !this.selectedElement.ref.closed;
        this.dirty = true;
        this.renderPage();
        this.renderParamsPanel();
        return;
      }
      const elemProp = target.getAttribute('data-elem-prop');
      const elemStep = target.getAttribute('data-elem-step');
      if (elemProp && elemStep) {
        this.adjustElementProp(this.selectedElement, elemProp, parseInt(elemStep, 10));
        return;
      }
    }

    // Tool-param branches below all key off `selectedTool` — when no
    // tool is active, they silently no-op.
    if (!this.selectedTool) return;

    const dir = target.getAttribute('data-param-dir');
    if (dir) {
      if (this.selectedTool === 'spike' || this.selectedTool === 'cannon') {
        this.toolParams[this.selectedTool] = dir as CardinalDir;
      } else if (this.selectedTool === 'conveyor') {
        this.toolParams.conveyor = dir as ConveyorDir;
      }
      this.renderParamsPanel();
      return;
    }

    const color = target.getAttribute('data-param-color');
    if (color !== null) {
      if (
        this.selectedTool === 'portal' ||
        this.selectedTool === 'key' ||
        this.selectedTool === 'key_wall'
      ) {
        this.toolParams[this.selectedTool] = parseInt(color, 10);
      }
      this.renderParamsPanel();
      return;
    }

    const step = target.getAttribute('data-param-step');
    if (step && this.selectedTool === 'teleport') {
      const max = this.level.pages.length - 1;
      const next = this.toolParams.teleport + parseInt(step, 10);
      this.toolParams.teleport = Math.max(0, Math.min(next, max));
      this.renderParamsPanel();
      return;
    }

    // Laser cannon params — direction (4 cardinals), rotate mode (none /
    // cw / ccw), and the duration / downtime steppers (0.5-second
    // increments; duration clamped at 0 = "continuous", downtime
    // clamped at 0.5 minimum).
    if (this.selectedTool === 'laser_cannon') {
      const lcDir = target.getAttribute('data-param-laser-dir');
      if (lcDir) {
        this.toolParams.laser_cannon.dir = lcDir as CardinalDir;
        this.renderParamsPanel();
        return;
      }
      const lcRot = target.getAttribute('data-param-laser-rotate');
      if (lcRot) {
        this.toolParams.laser_cannon.rotate = lcRot as LaserRotateMode;
        this.renderParamsPanel();
        return;
      }
      const lcDur = target.getAttribute('data-param-laser-dur');
      if (lcDur) {
        const delta = parseInt(lcDur, 10) * 0.5;
        const next = this.toolParams.laser_cannon.duration + delta;
        // Clamp at 0 (continuous) and a generous upper bound.
        this.toolParams.laser_cannon.duration = Math.max(0, Math.min(next, 30));
        this.renderParamsPanel();
        return;
      }
      const lcDown = target.getAttribute('data-param-laser-down');
      if (lcDown) {
        const delta = parseInt(lcDown, 10) * 0.5;
        const next = this.toolParams.laser_cannon.downtime + delta;
        // Min 0.5 — a 0-downtime non-continuous cycle would mean off-
        // for-zero, which is effectively continuous; the user has the
        // duration=0 path for that explicitly.
        this.toolParams.laser_cannon.downtime = Math.max(0.5, Math.min(next, 30));
        this.renderParamsPanel();
        return;
      }
    }

    // Text tool sticky params: width and height steppers (text content
    // is intentionally edited per-instance, not at placement time).
    // Min 1 cell each; we don't clamp the upper end here because the
    // page may be wider/taller than 30 — placement-time bounds check
    // (isAreaClearForText) catches out-of-page sizes.
    if (this.selectedTool === 'text') {
      const tw = target.getAttribute('data-param-text-w');
      if (tw) {
        const next = this.toolParams.text.width + parseInt(tw, 10);
        this.toolParams.text.width = Math.max(1, next);
        this.renderParamsPanel();
        return;
      }
      const th = target.getAttribute('data-param-text-h');
      if (th) {
        const next = this.toolParams.text.height + parseInt(th, 10);
        this.toolParams.text.height = Math.max(1, next);
        this.renderParamsPanel();
        return;
      }
    }
  }

  // Captures keystroke-by-keystroke updates to the per-instance text
  // textarea, written through to the selected element's `text` field
  // and reflected on the canvas. Crucially does NOT re-render the
  // params panel — that would destroy the textarea mid-typing.
  private onPaletteInput(ev: Event): void {
    const target = ev.target as HTMLElement;
    if (target.getAttribute('data-elem-text-input') === null) return;
    if (this.selectedElement?.kind !== 'text_label') return;
    this.selectedElement.ref.text = (target as HTMLTextAreaElement).value;
    this.dirty = true;
    this.renderPage();
  }

  private handlePaletteAction(action: string): void {
    switch (action) {
      case 'prev-page':
        this.changePage(-1);
        return;
      case 'next-page':
        this.changePage(1);
        return;
      case 'save':
        // Fire-and-forget — saveLevel() handles its own UI feedback.
        void this.saveLevel();
        return;
      case 'menu':
        if (this.dirty) {
          this.showUnsavedConfirm(
            // Save & Leave: stay in the editor if the save fails so
            // the user can fix whatever went wrong (network, etc.).
            async () => {
              const ok = await this.saveLevel();
              if (ok) this.leaveEditor();
            },
            // Discard & Leave: bail out without touching disk.
            () => this.leaveEditor(),
          );
          return;
        }
        this.leaveEditor();
        return;
      case 'play':
        // Pass dirty through so it survives the Play → Edit round
        // trip — otherwise the unsaved-changes prompt wouldn't fire
        // after the user plays then comes back to the editor.
        this.scene.start('PlayScene', {
          level: this.level,
          pageIndex: this.currentPageIndex,
          fromEditor: true,
          levelPath: this.levelPath,
          editorDirty: this.dirty,
          embedLevelId: this.embedLevelId ?? undefined,
        });
        return;
      case 'gear-finish-open':
        // Explicit "leave open" finalize — overrides any prior closed
        // flag so the user can re-open a previously-closed gear.
        if (this.gearEditState) {
          this.gearEditState.gear.closed = false;
          this.gearEditState = null;
          this.dirty = true;
        }
        this.renderPage();
        this.renderParamsPanel();
        return;
      case 'add-page':
        this.addPage();
        return;
      case 'del-page':
        this.deletePage();
        return;
    }
  }

  // Adds a blank page after the current page and switches to it. Tile
  // dimensions match the current page so cross-page navigation looks
  // consistent (the runtime doesn't formally require uniform sizes,
  // but mixing them would surprise authors). Spawn defaults to (1,1) —
  // the user will reposition with the spawn tool once walls are in.
  private addPage(): void {
    const current = this.level.pages[this.currentPageIndex];
    if (!current) return;
    this.dirty = true;
    const cols = current.tiles[0]!.length;
    const rows = current.tiles.length;
    const blank = '.'.repeat(cols);
    const newPage: PageData = {
      tiles: Array.from({ length: rows }, () => blank),
      spawn: { x: 1, y: 1 },
    };
    this.level.pages.splice(this.currentPageIndex + 1, 0, newPage);
    // Bump exit page index if the inserted page sits before the exit's
    // page. Teleports holding a target_page that points past the
    // insertion point also need a +1 nudge so they keep aiming at the
    // same logical page.
    if (this.level.exit.page > this.currentPageIndex) {
      this.level.exit.page += 1;
    }
    for (const pg of this.level.pages) {
      if (!pg.teleports) continue;
      for (const t of pg.teleports) {
        if (t.target_page > this.currentPageIndex) t.target_page += 1;
      }
    }
    this.currentPageIndex += 1;
    this.gearEditState = null;
    this.selectedElement = null;
    this.cancelDrag();
    this.renderPage();
    this.renderParamsPanel();
    this.updateLabels();
  }

  // Removes the current page after a confirm. Refuses when only one
  // page remains (a level needs at least one). Updates the exit and
  // any teleport target_page values:
  //   - exit on the deleted page  → reassigned to page 0
  //   - exit after the deleted page → index shifts -1
  //   - teleport target equals the deleted page → that teleport is
  //     dropped (the destination no longer exists)
  //   - teleport target after the deleted page → target shifts -1
  private deletePage(): void {
    if (this.level.pages.length <= 1) {
      window.alert('Cannot delete the last remaining page.');
      return;
    }
    const idx = this.currentPageIndex;
    const ok = window.confirm(
      `Delete page ${idx + 1}? This cannot be undone.`,
    );
    if (!ok) return;
    this.dirty = true;

    this.level.pages.splice(idx, 1);

    if (this.level.exit.page === idx) {
      this.level.exit.page = 0;
    } else if (this.level.exit.page > idx) {
      this.level.exit.page -= 1;
    }
    // Clamp exit (x, y) into its page's grid — defensive against
    // non-uniform page sizes in legacy levels. New pages added via
    // addPage match the current page's dims, so this is mostly a no-op.
    const exitPage = this.level.pages[this.level.exit.page];
    if (exitPage) {
      const cols = exitPage.tiles[0]!.length;
      const rows = exitPage.tiles.length;
      this.level.exit.x = Math.min(Math.max(0, this.level.exit.x), cols - 1);
      this.level.exit.y = Math.min(Math.max(0, this.level.exit.y), rows - 1);
    }

    for (const pg of this.level.pages) {
      if (!pg.teleports) continue;
      pg.teleports = pg.teleports.filter((t) => {
        if (t.target_page === idx) return false;
        if (t.target_page > idx) t.target_page -= 1;
        return true;
      });
    }

    if (this.currentPageIndex >= this.level.pages.length) {
      this.currentPageIndex = this.level.pages.length - 1;
    }
    this.gearEditState = null;
    this.selectedElement = null;
    this.cancelDrag();
    this.renderPage();
    this.renderParamsPanel();
    this.updateLabels();
  }

  private updateToolHighlight(): void {
    if (!this.palette) return;
    this.palette.querySelectorAll('[data-tool]').forEach((b) => {
      b.classList.toggle('selected', b.getAttribute('data-tool') === this.selectedTool);
    });
  }

  // --- params panel ------------------------------------------------------

  // Re-renders the params region below the tool grid for whichever tool
  // is currently selected. Empty for tools without editable params.
  private renderParamsPanel(): void {
    if (!this.paramsPanel) return;
    // Per-instance editing wins over tool params: when an element is
    // selected (auto-selected from a click on a placed object), its
    // own params panel takes priority regardless of which placement
    // tool is active.
    if (this.selectedElement) {
      this.paramsPanel.innerHTML = elementParamHtml(this.selectedElement, {
        pageCount: this.level.pages.length,
        isEditingGearPath:
          this.selectedElement.kind === 'gear' &&
          this.gearEditState?.gear === this.selectedElement.ref,
      });
      return;
    }
    if (!this.selectedTool) {
      this.paramsPanel.innerHTML = '';
      return;
    }
    switch (this.selectedTool) {
      case 'spike':
      case 'cannon':
        this.paramsPanel.innerHTML = dirParamHtml(
          this.toolParams[this.selectedTool],
          ['up', 'down', 'left', 'right'],
        );
        return;
      case 'conveyor':
        this.paramsPanel.innerHTML = dirParamHtml(this.toolParams.conveyor, ['cw', 'ccw']);
        return;
      case 'portal':
      case 'key':
      case 'key_wall':
        this.paramsPanel.innerHTML = colorParamHtml(this.toolParams[this.selectedTool]);
        return;
      case 'teleport':
        this.paramsPanel.innerHTML = teleportParamHtml(
          this.toolParams.teleport,
          this.level.pages.length,
        );
        return;
      case 'laser_cannon':
        this.paramsPanel.innerHTML = laserCannonParamHtml(this.toolParams.laser_cannon);
        return;
      case 'text':
        this.paramsPanel.innerHTML = textToolParamHtml(this.toolParams.text);
        return;
      default:
        this.paramsPanel.innerHTML = '';
    }
  }

  // Step a numeric property on the selected element by ±1 increment.
  // Each (kind, prop) pair has its own per-step amount and clamp.
  // Re-renders the page (some props affect visuals — gear size,
  // cannon dir) and the params panel (so the displayed value updates).
  private adjustElementProp(sel: SelectedElement, prop: string, dir: number): void {
    this.dirty = true;
    const clamp = (v: number, min: number, max: number) =>
      Math.max(min, Math.min(v, max));

    if (sel.kind === 'glass_wall' && prop === 'delay') {
      sel.ref.delay = clamp(sel.ref.delay + dir * 0.5, 0, 30);
    } else if (sel.kind === 'spike' || sel.kind === 'spike_block') {
      // duration: 0 → continuous; otherwise stepped 0.5s up to 30.
      // downtime: min 0.5s so a non-continuous cycle has a real off
      // phase (mirrors the laser's downtime clamp).
      // delay: 0 → no stagger; stepped 0.5s up to 30.
      if (prop === 'duration') {
        const cur = sel.ref.duration ?? 0;
        sel.ref.duration = clamp(cur + dir * 0.5, 0, 30);
      } else if (prop === 'downtime') {
        const cur = sel.ref.downtime ?? 3.0;
        sel.ref.downtime = clamp(cur + dir * 0.5, 0.5, 30);
      } else if (prop === 'delay') {
        const cur = sel.ref.delay ?? 0;
        sel.ref.delay = clamp(cur + dir * 0.5, 0, 30);
      }
    } else if (sel.kind === 'cannon') {
      if (prop === 'period') sel.ref.period = clamp(sel.ref.period + dir * 0.5, 0.5, 30);
      else if (prop === 'bullet_speed') sel.ref.bullet_speed = clamp(sel.ref.bullet_speed + dir * 0.5, 1, 30);
      else if (prop === 'delay') sel.ref.delay = clamp((sel.ref.delay ?? 0) + dir * 0.5, 0, 30);
    } else if (sel.kind === 'turret') {
      if (prop === 'period') sel.ref.period = clamp(sel.ref.period + dir * 0.5, 0.5, 30);
      else if (prop === 'bullet_speed') sel.ref.bullet_speed = clamp(sel.ref.bullet_speed + dir * 0.5, 1, 30);
      else if (prop === 'delay') sel.ref.delay = clamp((sel.ref.delay ?? 0) + dir * 0.5, 0, 30);
    } else if (sel.kind === 'gear') {
      if (prop === 'size') sel.ref.size = clamp(sel.ref.size + dir, 1, 4);
      else if (prop === 'speed') sel.ref.speed = clamp(sel.ref.speed + dir * 0.5, 0, 20);
      else if (prop === 'spin') sel.ref.spin = clamp(sel.ref.spin + dir * 0.5, 0, 20);
    } else if (sel.kind === 'teleport' && prop === 'target_page') {
      const max = this.level.pages.length - 1;
      sel.ref.target_page = clamp(sel.ref.target_page + dir, 0, max);
    } else if (sel.kind === 'laser_cannon') {
      if (prop === 'duration') sel.ref.duration = clamp(sel.ref.duration + dir * 0.5, 0, 30);
      else if (prop === 'downtime') sel.ref.downtime = clamp(sel.ref.downtime + dir * 0.5, 0.5, 30);
      else if (prop === 'delay') sel.ref.delay = clamp((sel.ref.delay ?? 0) + dir * 0.5, 0, 30);
    } else if (sel.kind === 'text_label') {
      // Width / height step by 1 cell; clamp at 1 minimum and the
      // current page's grid dimensions so the text never extends
      // past the level edge. Font size steps by 4 px in [8, 72] —
      // 16 is the default, 32 is "double", 48 is about a tile tall.
      const page = this.level.pages[this.currentPageIndex];
      const cols = page?.tiles[0]?.length ?? 1;
      const rows = page?.tiles.length ?? 1;
      if (prop === 'width') {
        sel.ref.width = clamp(sel.ref.width + dir, 1, cols - sel.ref.x);
      } else if (prop === 'height') {
        sel.ref.height = clamp(sel.ref.height + dir, 1, rows - sel.ref.y);
      } else if (prop === 'font_size') {
        const cur = sel.ref.font_size ?? 16;
        sel.ref.font_size = clamp(cur + dir * 4, 8, 72);
      }
    }

    this.renderPage();
    this.renderParamsPanel();
  }


  // --- pointer handlers --------------------------------------------------

  private onPointerDown(p: Phaser.Input.Pointer): void {
    const cell = this.cellAtPointer(p);
    if (!cell) return;

    // Eraser is the explicit "remove" tool — clicks/drags on filled
    // cells erase rather than auto-select.
    if (this.selectedTool === 'eraser') {
      this.applyTool('eraser', cell.col, cell.row);
      this.renderPage();
      this.placementDragState = { lastCol: cell.col, lastRow: cell.row };
      return;
    }

    // Auto-select gesture: clicking a filled cell (or any click while in
    // gear-path-edit) opens a gesture that discriminates click-vs-drag
    // in onPointerMove / onPointerUp. Gear-edit also routes through the
    // gesture so empty-cell clicks become waypoint-add operations.
    const page = this.level.pages[this.currentPageIndex];
    const occupied = page != null && this.isOccupied(page, cell.col, cell.row);
    if (occupied || this.gearEditState) {
      this.selectGesture = { start: cell, dragGhost: null };
      return;
    }

    // Empty cell, no gear-edit, no eraser. If a placement tool is active,
    // place; otherwise (pointer mode) treat the click as a deselect.
    if (this.selectedTool) {
      const placed = this.applyTool(this.selectedTool, cell.col, cell.row);
      if (placed) this.autoSelectPlaced(this.selectedTool, cell.col, cell.row);
      this.renderPage();
      if (DRAG_ENABLED_TOOLS.has(this.selectedTool)) {
        this.placementDragState = { lastCol: cell.col, lastRow: cell.row };
      }
      return;
    }
    if (this.selectedElement) {
      this.selectedElement = null;
      this.renderPage();
      this.renderParamsPanel();
    }
  }

  private onPointerMove(p: Phaser.Input.Pointer): void {
    if (this.selectGesture) {
      this.updateSelectGesture(p);
      return;
    }
    if (!this.placementDragState || !this.selectedTool) return;
    const cell = this.cellAtPointer(p);
    if (!cell) return;
    if (
      cell.col === this.placementDragState.lastCol &&
      cell.row === this.placementDragState.lastRow
    ) return;
    // applyTool silently no-ops on occupied cells (issue #3), so a drag
    // streak naturally skips over existing elements without overwriting.
    const placed = this.applyTool(this.selectedTool, cell.col, cell.row);
    if (placed) this.autoSelectPlaced(this.selectedTool, cell.col, cell.row);
    this.renderPage();
    this.placementDragState.lastCol = cell.col;
    this.placementDragState.lastRow = cell.row;
  }

  private onPointerUp(p: Phaser.Input.Pointer): void {
    if (this.selectGesture) {
      this.finishSelectGesture(p);
      return;
    }
    this.placementDragState = null;
  }

  // Translate a pointer event to a tile cell, returning null if the
  // pointer is outside the level grid.
  private cellAtPointer(
    p: Phaser.Input.Pointer,
  ): { col: number; row: number } | null {
    const col = Math.floor(p.worldX / TILE_SIZE);
    const row = Math.floor(p.worldY / TILE_SIZE);
    const page = this.level.pages[this.currentPageIndex];
    if (!page) return null;
    const cols = page.tiles[0]!.length;
    const rows = page.tiles.length;
    if (col < 0 || col >= cols || row < 0 || row >= rows) return null;
    return { col, row };
  }

  // --- Select-tool gesture (click vs drag) -------------------------------

  // Pointermove while a Select gesture is open. The first time the
  // pointer leaves the start cell, if the start cell holds something
  // movable we arm drag mode by creating the ghost. Drag works
  // regardless of gear-edit state — moving an element doesn't conflict
  // with waypoint-add (which only fires on a click + same cell).
  private updateSelectGesture(p: Phaser.Input.Pointer): void {
    if (!this.selectGesture) return;
    const col = Math.floor(p.worldX / TILE_SIZE);
    const row = Math.floor(p.worldY / TILE_SIZE);

    const onStartCell =
      col === this.selectGesture.start.col && row === this.selectGesture.start.row;

    if (!this.selectGesture.dragGhost && !onStartCell) {
      const page = this.level.pages[this.currentPageIndex];
      const startHasElement =
        page != null &&
        this.isOccupied(page, this.selectGesture.start.col, this.selectGesture.start.row);
      if (startHasElement) {
        const ghost = this.add.rectangle(
          (col + 0.5) * TILE_SIZE,
          (row + 0.5) * TILE_SIZE,
          TILE_SIZE,
          TILE_SIZE,
          0xffffff,
        );
        ghost.setAlpha(0.35);
        ghost.setStrokeStyle(2, 0xffffff, 0.85);
        ghost.setDepth(1000);
        this.selectGesture.dragGhost = ghost;
      }
    }

    if (this.selectGesture.dragGhost) {
      this.selectGesture.dragGhost.setPosition(
        (col + 0.5) * TILE_SIZE,
        (row + 0.5) * TILE_SIZE,
      );
    }
  }

  // Resolves the gesture on pointerup. If the ghost was armed, this
  // is a drag — move the element. Otherwise it's a click — but only
  // honored when the up cell matches the down cell, so a half-finished
  // drag (start had no element, pointer wandered) is a silent cancel
  // rather than a misfire.
  private finishSelectGesture(p: Phaser.Input.Pointer): void {
    if (!this.selectGesture) return;
    const target = this.cellAtPointer(p);
    const page = this.level.pages[this.currentPageIndex];

    if (this.selectGesture.dragGhost) {
      if (target && page) {
        const sameCell =
          target.col === this.selectGesture.start.col &&
          target.row === this.selectGesture.start.row;
        if (!sameCell) {
          // Multi-cell elements (text_label) need a bounds-check
          // against the new area, ignoring cells the text already
          // occupies. Single-cell elements just check the drop cell.
          const startElem = this.findElementAt(
            page,
            this.selectGesture.start.col,
            this.selectGesture.start.row,
          );
          let canMove = false;
          if (startElem?.kind === 'text_label') {
            const dCol = target.col - this.selectGesture.start.col;
            const dRow = target.row - this.selectGesture.start.row;
            canMove = this.isAreaClearForText(
              page,
              startElem.ref.x + dCol,
              startElem.ref.y + dRow,
              startElem.ref.width,
              startElem.ref.height,
              startElem.ref,
            );
          } else {
            canMove = !this.isOccupied(page, target.col, target.row);
          }
          if (canMove) {
            // For wall/coin selections, the synthetic ref doesn't
            // share identity with anything in the level data, so
            // moveElementAt's tile-swap branch can't update it for
            // us. Capture whether the currently-selected element
            // sits at the source cell BEFORE the move, so we can
            // re-anchor it to the destination after. Array-backed
            // elements get their x/y mutated in-place, so the same
            // re-anchor is a redundant no-op for them.
            const selectionAtSource =
              this.selectedElement?.ref.x === this.selectGesture.start.col &&
              this.selectedElement?.ref.y === this.selectGesture.start.row;
            this.moveElementAt(
              page,
              this.selectGesture.start.col,
              this.selectGesture.start.row,
              target.col,
              target.row,
            );
            if (selectionAtSource && this.selectedElement) {
              this.selectedElement.ref.x = target.col;
              this.selectedElement.ref.y = target.row;
            }
          }
        }
      }
      this.selectGesture.dragGhost.destroy();
    } else if (
      target &&
      target.col === this.selectGesture.start.col &&
      target.row === this.selectGesture.start.row
    ) {
      this.handleSelectClick(target.col, target.row);
    }

    this.selectGesture = null;
    this.renderPage();
  }

  // Click router for Select-tool clicks (no drag). Two layers:
  //   1. If a gear-edit session is open, the click is interpreted as a
  //      waypoint operation (close on home, switch on another gear,
  //      push on empty, ignore on other occupied).
  //   2. Otherwise, the click selects an element under the cursor (or
  //      deselects on empty).
  // Selecting a gear ALSO opens its waypoint-edit session, so the two
  // states stay in sync for the gear case.
  private handleSelectClick(col: number, row: number): void {
    const page = this.level.pages[this.currentPageIndex];
    if (!page) return;

    if (this.gearEditState) {
      const gear = this.gearEditState.gear;

      if (gear.x === col && gear.y === row) {
        // Click on edited-gear's home → close loop + exit edit (selection stays).
        gear.closed = true;
        this.dirty = true;
        this.gearEditState = null;
        this.renderParamsPanel();
        return;
      }

      const otherGear = page.gears?.find((g) => g.x === col && g.y === row);
      if (otherGear) {
        // Switch waypoint target AND selection.
        this.gearEditState = { gear: otherGear };
        this.selectedElement = { kind: 'gear', ref: otherGear };
        this.renderParamsPanel();
        return;
      }

      // Click on a non-gear element while editing → switch selection,
      // exit gear-edit (selection-of-element takes priority over the
      // waypoint flow when the click target is editable).
      const otherElement = this.findElementAt(page, col, row);
      if (otherElement) {
        this.gearEditState = null;
        this.selectedElement = otherElement;
        this.renderParamsPanel();
        return;
      }

      // Empty cell while editing → add waypoint.
      if (this.isOccupied(page, col, row)) return;
      if (gear.waypoints.some((w) => w.x === col && w.y === row)) return;
      gear.waypoints.push({ x: col, y: row });
      this.dirty = true;
      return;
    }

    // No gear-edit session — try to select an element at this cell.
    const found = this.findElementAt(page, col, row);
    if (found) {
      this.selectedElement = found;
      // Selecting a gear also opens path-edit — preserves the prior
      // "click gear to edit waypoints" UX.
      if (found.kind === 'gear') {
        this.gearEditState = { gear: found.ref };
      }
      this.renderParamsPanel();
      return;
    }

    // Empty cell with no edit session: deselect.
    if (this.selectedElement) {
      this.selectedElement = null;
      this.renderParamsPanel();
    }
  }

  // Used by shutdown / destroy / page-change to clean up an in-progress
  // gesture without mutating the level data.
  private cancelDrag(): void {
    this.placementDragState = null;
    if (this.selectGesture) {
      this.selectGesture.dragGhost?.destroy();
      this.selectGesture = null;
    }
  }

  // After a successful applyTool placement, select the just-placed
  // element so the full per-instance params panel is editable without
  // the user having to click the new element. Gated by AUTO_SELECT_TOOLS
  // so structural placements (wall, coin, spawn, exit, portal) don't
  // hijack the panel with a header-only display. The caller is
  // responsible for the trailing renderPage() that actually draws the
  // resulting selection highlight.
  private autoSelectPlaced(tool: ToolId, col: number, row: number): void {
    if (!AUTO_SELECT_TOOLS.has(tool)) return;
    const page = this.level.pages[this.currentPageIndex];
    if (!page) return;
    const sel = this.findElementAt(page, col, row);
    if (!sel) return;
    this.selectedElement = sel;
    this.renderParamsPanel();
  }

  // Returns a SelectedElement wrapper around whatever the user clicked
  // at (col, row), or null if the cell is empty. Walls (W) and coins
  // (C) are tile-string chars with no array ref, so we synthesise a
  // `{x, y}` ref for them — the panel just shows the kind, but the
  // highlight + drag-move work the same as for array-backed elements.
  // Portals are skipped — color is shared per pair, and both points
  // already drag-move individually.
  private findElementAt(page: PageData, col: number, row: number): SelectedElement | null {
    const at = (e: { x: number; y: number }) => e.x === col && e.y === row;
    let e;
    if ((e = page.glass_walls?.find(at))) return { kind: 'glass_wall', ref: e };
    if ((e = page.spike_blocks?.find(at))) return { kind: 'spike_block', ref: e };
    if ((e = page.spikes?.find(at))) return { kind: 'spike', ref: e };
    if ((e = page.conveyors?.find(at))) return { kind: 'conveyor', ref: e };
    if ((e = page.cannons?.find(at))) return { kind: 'cannon', ref: e };
    if ((e = page.turrets?.find(at))) return { kind: 'turret', ref: e };
    if ((e = page.gears?.find(at))) return { kind: 'gear', ref: e };
    if ((e = page.keys?.find(at))) return { kind: 'key', ref: e };
    if ((e = page.key_walls?.find(at))) return { kind: 'key_wall', ref: e };
    if ((e = page.teleports?.find(at))) return { kind: 'teleport', ref: e };
    if ((e = page.laser_cannons?.find(at))) return { kind: 'laser_cannon', ref: e };
    // Text labels are multi-cell — the click matches if (col, row) is
    // anywhere in their bounds rect, not just the top-left anchor.
    const tl = page.text_labels?.find(
      (t) => col >= t.x && col < t.x + t.width && row >= t.y && row < t.y + t.height,
    );
    if (tl) return { kind: 'text_label', ref: tl };
    // Singleton positions (start, exit). Live refs into the level data
    // so drag-move auto-tracks via in-place mutation.
    if (page.spawn.x === col && page.spawn.y === row) {
      return { kind: 'spawn', ref: page.spawn };
    }
    if (
      this.level.exit.page === this.currentPageIndex &&
      this.level.exit.x === col &&
      this.level.exit.y === row
    ) {
      return { kind: 'exit', ref: this.level.exit };
    }
    // Tile-char fallback. Lowest priority so an array-backed element
    // sitting on the same cell (which shouldn't happen, but defensive)
    // wins.
    const ch = page.tiles[row]?.charAt(col);
    if (ch === 'W') return { kind: 'wall', ref: { x: col, y: row } };
    if (ch === 'C') return { kind: 'coin', ref: { x: col, y: row } };
    return null;
  }

  // After any operation that may have removed elements (eraseAt, page
  // change), drop selectedElement if its ref is no longer present in
  // the current page's arrays.
  private validateSelectedElement(): void {
    if (!this.selectedElement) return;
    const page = this.level.pages[this.currentPageIndex];
    if (!page) {
      this.selectedElement = null;
      return;
    }
    const sel = this.selectedElement;
    // Wall / coin selections are tile-char backed: the synthetic ref
    // isn't stored anywhere, so `includes` would never match. Check
    // the tile char at the recorded cell instead.
    if (sel.kind === 'wall' || sel.kind === 'coin') {
      const ch = page.tiles[sel.ref.y]?.charAt(sel.ref.x);
      const expected = sel.kind === 'wall' ? 'W' : 'C';
      if (ch !== expected) this.selectedElement = null;
      return;
    }
    // Singletons: spawn always exists on its page; exit may have been
    // erased (sentinel page = -1) — clear in that case.
    if (sel.kind === 'spawn') {
      if (sel.ref !== page.spawn) this.selectedElement = null;
      return;
    }
    if (sel.kind === 'exit') {
      if (sel.ref !== this.level.exit || this.level.exit.page !== this.currentPageIndex) {
        this.selectedElement = null;
      }
      return;
    }
    const ref = sel.ref;
    const present =
      page.glass_walls?.includes(ref as GlassWallData) ||
      page.spike_blocks?.includes(ref as SpikeBlockData) ||
      page.spikes?.includes(ref as SpikeData) ||
      page.conveyors?.includes(ref as ConveyorData) ||
      page.cannons?.includes(ref as CannonData) ||
      page.turrets?.includes(ref as TurretData) ||
      page.gears?.includes(ref as GearData) ||
      page.keys?.includes(ref as KeyData) ||
      page.key_walls?.includes(ref as KeyWallData) ||
      page.teleports?.includes(ref as TeleportData) ||
      page.laser_cannons?.includes(ref as LaserCannonData) ||
      page.text_labels?.includes(ref as TextLabelData);
    if (!present) {
      this.selectedElement = null;
    }
  }

  // True if this cell holds anything that would collide with a new
  // placement: a tile char (wall/coin), any per-page element, the spawn
  // marker, or this page's exit. Used both to reject placement (issue #3)
  // and to decide whether a Select-tool click has something to grab.
  private isOccupied(page: PageData, col: number, row: number): boolean {
    const ch = page.tiles[row]?.charAt(col);
    if (ch === 'W' || ch === 'C') return true;

    const at = (e: { x: number; y: number }) => e.x === col && e.y === row;
    if (page.glass_walls?.some(at)) return true;
    if (page.spike_blocks?.some(at)) return true;
    if (page.spikes?.some(at)) return true;
    if (page.conveyors?.some(at)) return true;
    if (page.cannons?.some(at)) return true;
    if (page.key_walls?.some(at)) return true;
    if (page.keys?.some(at)) return true;
    if (page.turrets?.some(at)) return true;
    if (page.laser_cannons?.some(at)) return true;
    if (page.teleports?.some(at)) return true;
    if (page.gears?.some(at)) return true;
    if (page.portals?.some((p) => p.points.some(at))) return true;
    // Text labels span multi-cell bounds; any cell inside counts.
    if (page.text_labels?.some(
      (t) => col >= t.x && col < t.x + t.width && row >= t.y && row < t.y + t.height,
    )) return true;

    if (page.spawn.x === col && page.spawn.y === row) return true;
    if (
      this.level.exit.page === this.currentPageIndex &&
      this.level.exit.x === col &&
      this.level.exit.y === row
    ) return true;

    return false;
  }

  // Move whatever's at (srcCol, srcRow) to (dstCol, dstRow). Caller is
  // responsible for ensuring the target is unoccupied. For wall/coin
  // tiles, swaps the char in the row strings; for everything else,
  // mutates the matching element's x/y in place. Gear waypoints and
  // unselected portal points stay where they are — only the clicked
  // entity moves.
  private moveElementAt(
    page: PageData,
    srcCol: number,
    srcRow: number,
    dstCol: number,
    dstRow: number,
  ): void {
    this.dirty = true;
    const at = (e: { x: number; y: number }) => e.x === srcCol && e.y === srcRow;
    const move = (e: { x: number; y: number }) => {
      e.x = dstCol;
      e.y = dstRow;
    };

    const r = page.tiles[srcRow]!;
    const ch = r.charAt(srcCol);
    if (ch === 'W' || ch === 'C') {
      page.tiles[srcRow] = r.slice(0, srcCol) + '.' + r.slice(srcCol + 1);
      const r2 = page.tiles[dstRow]!;
      page.tiles[dstRow] = r2.slice(0, dstCol) + ch + r2.slice(dstCol + 1);
      return;
    }

    page.glass_walls?.forEach((e) => { if (at(e)) move(e); });
    page.spike_blocks?.forEach((e) => { if (at(e)) move(e); });
    page.spikes?.forEach((e) => { if (at(e)) move(e); });
    page.conveyors?.forEach((e) => { if (at(e)) move(e); });
    page.cannons?.forEach((e) => { if (at(e)) move(e); });
    page.key_walls?.forEach((e) => { if (at(e)) move(e); });
    page.keys?.forEach((e) => { if (at(e)) move(e); });
    page.turrets?.forEach((e) => { if (at(e)) move(e); });
    page.laser_cannons?.forEach((e) => { if (at(e)) move(e); });
    page.teleports?.forEach((e) => { if (at(e)) move(e); });
    page.gears?.forEach((g) => { if (at(g)) move(g); });
    page.portals?.forEach((p) =>
      p.points.forEach((pt) => { if (at(pt)) move(pt); }),
    );
    // Text labels: multi-cell, so we shift by the (dst − src) delta
    // rather than teleporting the top-left to the cursor cell. The
    // click cell-vs-text-bounds test picks the right text when the
    // user grabs anywhere inside its area.
    const dCol = dstCol - srcCol;
    const dRow = dstRow - srcRow;
    page.text_labels?.forEach((t) => {
      if (
        srcCol >= t.x && srcCol < t.x + t.width &&
        srcRow >= t.y && srcRow < t.y + t.height
      ) {
        t.x += dCol;
        t.y += dRow;
      }
    });

    if (page.spawn.x === srcCol && page.spawn.y === srcRow) {
      move(page.spawn);
    }
    if (
      this.level.exit.page === this.currentPageIndex &&
      this.level.exit.x === srcCol &&
      this.level.exit.y === srcRow
    ) {
      this.level.exit.x = dstCol;
      this.level.exit.y = dstRow;
    }
  }

  // --- tool dispatch -----------------------------------------------------

  // Single placement entry point. Refuses to overwrite — issue #3 — so
  // any cell with an existing element rejects new placements (the user
  // must erase first). Eraser is its own branch. All directional /
  // colored / target-bearing tools read their settings from `toolParams`.
  // Returns true iff a placement actually wrote something to the page,
  // so the caller can auto-select the newly-placed element (giving the
  // user immediate access to its full per-instance params panel without
  // needing a follow-up click). Eraser + occupied-cell rejections return
  // false so the caller skips the auto-select.
  private applyTool(tool: ToolId, col: number, row: number): boolean {
    const page = this.level.pages[this.currentPageIndex]!;

    if (tool === 'eraser') { this.eraseAt(page, col, row); return false; }

    if (this.isOccupied(page, col, row)) return false;

    if (tool === 'spawn')  {
      page.spawn = { x: col, y: row };
      this.dirty = true;
      return true;
    }
    if (tool === 'exit')   {
      this.level.exit = { page: this.currentPageIndex, x: col, y: row };
      this.dirty = true;
      return true;
    }

    // `mutated` lets us mark dirty exactly once when a branch actually
    // appended/wrote — avoiding a false-positive dirty when the text
    // branch's bounds-check rejects.
    let mutated = false;
    switch (tool) {
      case 'wall':
        this.setTile(page, col, row, 'W');
        mutated = true;
        break;
      case 'coin':
        this.setTile(page, col, row, 'C');
        mutated = true;
        break;
      case 'glass':
        this.appendTo(page, 'glass_walls', { x: col, y: row, delay: DEFAULT_GLASS_DELAY });
        mutated = true;
        break;
      case 'spike_block':
        // duration 0 = always extended (legacy behavior). User can
        // step it up via per-instance editing to enable the cycle.
        this.appendTo(page, 'spike_blocks', { x: col, y: row, duration: 0, downtime: 3.0 });
        mutated = true;
        break;
      case 'spike':
        this.appendTo(page, 'spikes', {
          x: col, y: row, dir: this.toolParams.spike, duration: 0, downtime: 3.0,
        });
        mutated = true;
        break;
      case 'conveyor':
        this.appendTo(page, 'conveyors', { x: col, y: row, dir: this.toolParams.conveyor });
        mutated = true;
        break;
      case 'cannon':
        this.appendTo(page, 'cannons', {
          x: col, y: row, dir: this.toolParams.cannon,
          period: DEFAULT_CANNON_PERIOD, bullet_speed: DEFAULT_CANNON_BULLET_SPEED,
        });
        mutated = true;
        break;
      case 'turret':
        this.appendTo(page, 'turrets', {
          x: col, y: row, period: DEFAULT_TURRET_PERIOD, bullet_speed: DEFAULT_TURRET_BULLET_SPEED,
        });
        mutated = true;
        break;
      case 'laser_cannon': {
        const lc = this.toolParams.laser_cannon;
        this.appendTo(page, 'laser_cannons', {
          x: col, y: row,
          dir: lc.dir,
          rotate: lc.rotate,
          duration: lc.duration,
          downtime: lc.downtime,
        });
        mutated = true;
        break;
      }
      case 'gear':
        this.appendTo(page, 'gears', {
          x: col, y: row, size: DEFAULT_GEAR_SIZE, speed: DEFAULT_GEAR_SPEED,
          spin: DEFAULT_GEAR_SPIN, waypoints: [], closed: false,
        });
        mutated = true;
        break;
      case 'portal':
        this.placePortalPoint(page, col, row, this.toolParams.portal);
        mutated = true;
        break;
      case 'key':
        this.appendTo(page, 'keys', { x: col, y: row, color: this.toolParams.key });
        mutated = true;
        break;
      case 'key_wall':
        this.appendTo(page, 'key_walls', { x: col, y: row, color: this.toolParams.key_wall });
        mutated = true;
        break;
      case 'teleport': {
        const max = this.level.pages.length - 1;
        const target = Math.max(0, Math.min(this.toolParams.teleport, max));
        this.appendTo(page, 'teleports', { x: col, y: row, target_page: target });
        mutated = true;
        break;
      }
      case 'text': {
        const tp = this.toolParams.text;
        // Multi-cell placement: every cell in the bounds must be free
        // (rule #3). The top-left was already checked by the outer
        // applyTool guard, so this re-checks every cell including the
        // top-left to keep the loop simple.
        if (!this.isAreaClearForText(page, col, row, tp.width, tp.height, null)) {
          break;
        }
        this.appendTo(page, 'text_labels', {
          x: col, y: row, width: tp.width, height: tp.height, text: 'Text',
        });
        mutated = true;
        break;
      }
    }
    if (mutated) this.dirty = true;
    return mutated;
  }

  // Multi-cell area-clear check used by text-label placement and drag-
  // move. Walks each cell of the proposed bounds, rejecting if any
  // cell is occupied — except cells already inside `excludeText` (the
  // text being moved), which are treated as vacant since the text
  // will leave those positions.
  private isAreaClearForText(
    page: PageData,
    x: number,
    y: number,
    w: number,
    h: number,
    excludeText: TextLabelData | null,
  ): boolean {
    const cols = page.tiles[0]?.length ?? 0;
    const rows = page.tiles.length;
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        const cx = x + dx;
        const cy = y + dy;
        if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) return false;
        if (excludeText &&
            cx >= excludeText.x && cx < excludeText.x + excludeText.width &&
            cy >= excludeText.y && cy < excludeText.y + excludeText.height) {
          continue;
        }
        if (this.isOccupied(page, cx, cy)) return false;
      }
    }
    return true;
  }

  // Lazy-init optional element arrays so the saved JSON doesn't carry
  // empty arrays for element types the level doesn't use.
  private appendTo<K extends keyof PageData>(
    page: PageData,
    key: K,
    item: NonNullable<PageData[K]> extends Array<infer U> ? U : never,
  ): void {
    const arr = (page[key] as unknown as unknown[] | undefined) ?? [];
    arr.push(item);
    (page as unknown as Record<string, unknown>)[key as string] = arr;
  }

  // Tile grid is row-major strings; immutable per-row so we slice + reinsert.
  private setTile(page: PageData, col: number, row: number, ch: string): void {
    const r = page.tiles[row]!;
    page.tiles[row] = r.slice(0, col) + ch + r.slice(col + 1);
  }

  // Portals stack: each color holds at most one portal entry, with up to
  // 2 points. First click on a color creates an orphan; second click on
  // the same color pairs it; third+ click on a paired portal replaces the
  // SECOND point (so the first half stays put — feels more like "moving
  // the destination" than reshuffling both halves).
  private placePortalPoint(
    page: PageData,
    col: number,
    row: number,
    color: number,
  ): void {
    if (!page.portals) page.portals = [];
    let portal = page.portals.find((p) => p.color === color);
    if (!portal) {
      portal = { color, points: [] };
      page.portals.push(portal);
    }
    if (portal.points.some((pt) => pt.x === col && pt.y === row)) return;
    if (portal.points.length >= 2) {
      portal.points = [portal.points[0]!, { x: col, y: row }];
    } else {
      portal.points.push({ x: col, y: row });
    }
  }

  // Cell-clear: tile back to '.' and any single-cell element at this cell
  // removed. Multi-cell entities (gears with waypoints, portals with two
  // points) get their HOME / a single point removed; gears keyed off
  // their home cell, portals filter the matching point and prune empty
  // portals. Spawn is NOT erased — every level needs a starting cell,
  // and there's no concept of "no spawn". Exit IS erasable: setting
  // its page to -1 is the project's "no active exit" sentinel that
  // every consumer (rendering, occupancy, drag-move, runtime trigger)
  // already filters out via the `exit.page === currentPageIndex`
  // guard. The user re-places via the Exit tool when they want one.
  //
  // Cascade: erasing a key whose color has no other keys left in the
  // level also wipes all key_walls of that color (issue #2). Multiple
  // keys of the same color are uncommon but legal — only the LAST key
  // of a color triggers the wall sweep so a stray duplicate-key delete
  // doesn't silently break the rest of the puzzle.
  private eraseAt(page: PageData, col: number, row: number): void {
    // Conservatively mark dirty: even drag-eraser passes over empty
    // cells will set this, but the worst case is one extra "Save?"
    // prompt — well worth not having to detect "did anything change"
    // across all the per-array filter calls below.
    this.dirty = true;
    const at = (e: { x: number; y: number }) => e.x === col && e.y === row;
    const r = page.tiles[row]!;
    if (r.charAt(col) !== '.') {
      page.tiles[row] = r.slice(0, col) + '.' + r.slice(col + 1);
    }

    // Snapshot key colors at this cell BEFORE removing them so we can
    // run the orphan-walls sweep below.
    const removedKeyColors = page.keys?.filter(at).map((k) => k.color) ?? [];

    if (page.glass_walls)  page.glass_walls  = page.glass_walls.filter((e) => !at(e));
    if (page.spike_blocks) page.spike_blocks = page.spike_blocks.filter((e) => !at(e));
    if (page.spikes)       page.spikes       = page.spikes.filter((e) => !at(e));
    if (page.conveyors)    page.conveyors    = page.conveyors.filter((e) => !at(e));
    if (page.cannons)      page.cannons      = page.cannons.filter((e) => !at(e));
    if (page.key_walls)    page.key_walls    = page.key_walls.filter((e) => !at(e));
    if (page.keys)         page.keys         = page.keys.filter((e) => !at(e));
    if (page.turrets)      page.turrets      = page.turrets.filter((e) => !at(e));
    if (page.laser_cannons) page.laser_cannons = page.laser_cannons.filter((e) => !at(e));
    if (page.teleports)    page.teleports    = page.teleports.filter((e) => !at(e));
    if (page.gears) {
      page.gears = page.gears.filter((g) => !at(g));
      // Drop the path-edit reference if the gear under it just got wiped.
      if (this.gearEditState && !page.gears.includes(this.gearEditState.gear)) {
        this.gearEditState = null;
      }
    }
    if (page.portals) {
      page.portals = page.portals
        .map((p) => ({ ...p, points: p.points.filter((pt) => !at(pt)) }))
        .filter((p) => p.points.length > 0);
    }
    // Text labels: erase any whose bounds rect contains (col, row).
    if (page.text_labels) {
      page.text_labels = page.text_labels.filter(
        (t) => !(col >= t.x && col < t.x + t.width && row >= t.y && row < t.y + t.height),
      );
    }

    for (const color of removedKeyColors) {
      const stillHasKey = this.level.pages.some((pg) =>
        pg.keys?.some((k) => k.color === color),
      );
      if (stillHasKey) continue;
      for (const pg of this.level.pages) {
        if (pg.key_walls) {
          pg.key_walls = pg.key_walls.filter((w) => w.color !== color);
        }
      }
    }

    // Exit erase. Sentinel page -1 = "no active exit". The existing
    // `exit.page === currentPageIndex` check at every consumer (the
    // editor's drawExit / isOccupied / moveElementAt branches, plus
    // PlayScene.buildExit / handleExit) naturally short-circuits when
    // page is negative, so no other code needs updating.
    if (
      this.level.exit.page === this.currentPageIndex &&
      this.level.exit.x === col &&
      this.level.exit.y === row
    ) {
      this.level.exit = { page: -1, x: 0, y: 0 };
    }

    this.validateSelectedElement();
  }
}
